import {
  type CloudAccountChangeParams,
  useGetAllUsers,
  LayoutWrapper,
  extractAccountIdFromCombinedId,
  getSelectedAccountIdWithCloud,
  useAccountId,
  startLoading,
  stopLoading,
  LoadingTypes,
  useCurrentCloudProvider,
  useCloseSalesforceModalAfterSave,
  isLoading as Loading,
  type CloudType,
  type AccountDataSelection as AccountData,
  loadAvailableAccounts,
  setSelectedAccount,
  actionTypeWrapper,
  useSalesforceContext,
  useUserType,
  SelectedCloudIcon,
  PartnerType,
  type AccountDataState,
  getAccount,
} from '@labrav/common'
import React, { useCallback, useState, useMemo, useEffect } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { ModalToCreateResaleAuthorization } from '../../common/components/ModalToCreateResaleAuthorization/ModalToCreateResaleAuthorization'
import { ResellerAuthorizationCreation } from '../../components/AddEditResellerAuthorization'
import { ViewSentResellerAuthorization } from '../../components/ResellerAuthorizations'
import {
  setCloudMarketplaceProductListing,
  updateProductIdOnFilters,
} from '../../modules/productListingFilter/action'
import {
  FlyoutFunction,
  type CloudMarketplace,
} from '../../modules/productsListing/types'
import { EditResaleAuthorizationWrapperV2 } from '../../components/ResaleAuthorizationV2'
import {
  clearProductsListing,
  getProducts,
  setSelectedProductOnListing,
} from '../../modules/productsListing/action'
import { RootState, useDispatch, useSelector } from '../../store'
import { getCurrentResaleAuthorization } from '../../admin/modules/resaleAuthorizations/actions'
import { isEmpty } from 'lodash'
import { LoadingLogo } from '@labrav/react-components'
import { useCommonStyles } from '../../common/styles/common.styles'

const SalesforceEditResaleAuthorizationV2 = (
  props: Omit<
    React.ComponentProps<typeof EditResaleAuthorizationWrapperV2>,
    'users'
  >
) => {
  const partnerType = useUserType()
  const { users } = useGetAllUsers(partnerType)
  return <EditResaleAuthorizationWrapperV2 {...props} users={users} />
}

const SalesforceResellerAuthorizationCreationAzure = (
  props: Omit<
    React.ComponentProps<typeof ResellerAuthorizationCreation>,
    'users'
  >
) => {
  const partnerType = useUserType()
  const { users } = useGetAllUsers(partnerType)
  return <ResellerAuthorizationCreation {...props} users={users} />
}

export const ResellerAuthCreationModal: React.FC = () => {
  const [selectedProductId, setSelectedProductId] = useState<string>('')
  const navigate = useNavigate()
  const {
    partner_id: accountIdFromParams,
    cloud: routeCloud,
    accountId: accountIdFromRoute,
  } = useParams<{
    partner_id?: string
    cloud?: string
    accountId?: string
  }>()
  const dispatch = useDispatch()
  const { search } = useLocation()
  const selectedCloudTypeProductListing = useCurrentCloudProvider() as CloudType
  const accountIdFromHook = useAccountId({ product: 'flyout' }) || ''
  const accountId =
    accountIdFromRoute || accountIdFromParams || accountIdFromHook || ''

  const accounts = useSelector<AccountData[]>(
    (state: RootState) =>
      state.accountSelection.availableAccounts as AccountData[]
  )
  const cloudAccountId = new URLSearchParams(search).get('cloudAccountId') || ''
  const currentAccountId = cloudAccountId || accountId
  // Get the selected account ID with cloud for matching in dropdown
  const selectedAccountIdWithCloud = getSelectedAccountIdWithCloud(
    currentAccountId,
    'flyout',
    routeCloud || selectedCloudTypeProductListing
  )
  const cloudMarketplace: CloudMarketplace = routeCloud
    ? (routeCloud.toUpperCase() as CloudMarketplace)
    : selectedCloudTypeProductListing

  useEffect(() => {
    if (cloudMarketplace) {
      const accountData = (Array.isArray(accounts) ? accounts : []).filter(
        (account: AccountData) => account?.product?.toLowerCase() === 'flyout'
      )
      const initialAccountId =
        accountData?.find(
          (account: AccountData) => account?.cloud === cloudMarketplace
        )?.id || ''
      dispatch(
        setSelectedAccount(
          initialAccountId,
          'flyout',
          cloudMarketplace as CloudType
        )
      )
    }
  }, [])

  const handleCreateResaleAuthorization = () => {
    const externalBaseURL = `/external-apps/salesforce/partners/${cloudAccountId || accountId}/flyout`
    navigate({
      pathname: `${externalBaseURL}/products-listing/${selectedProductId}/resale-authorizations/edit`,
      search: search,
    })
  }

  const handleProductChange = useCallback(
    async (productId: string) => {
      setSelectedProductId(productId)

      dispatch(
        updateProductIdOnFilters(
          productId,
          selectedCloudTypeProductListing,
          FlyoutFunction.RESALE_AUTHORIZATIONS
        )
      )
    },
    [dispatch, selectedCloudTypeProductListing]
  )

  const handleCloudAccountChange = useCallback(
    async (params: CloudAccountChangeParams) => {
      // Extract original accountId from the combined format "accountId-product-cloud"
      const extractedAccountId = extractAccountIdFromCombinedId(
        params.accountId
      )
      const { cloud } = params

      await dispatch(startLoading(LoadingTypes.GENERAL))
      await dispatch(
        setSelectedAccount(extractedAccountId, 'flyout', cloud as CloudType)
      )
      await dispatch(
        actionTypeWrapper(
          cloudMarketplace,
          setSelectedProductOnListing(undefined)
        )
      )
      await dispatch(actionTypeWrapper(cloud, clearProductsListing()))
      await dispatch(setCloudMarketplaceProductListing(cloud))
      // await dispatch(clearPrivateOffers())
      await dispatch(getProducts(extractedAccountId, cloud))
      await dispatch(stopLoading(LoadingTypes.GENERAL))
    },
    [dispatch, cloudMarketplace]
  )

  return (
    <ModalToCreateResaleAuthorization
      open={true}
      hideCancelIcon={true}
      handleCreateResaleAuthorization={handleCreateResaleAuthorization}
      handleProductChange={handleProductChange}
      selectedAccountIdWithCloud={selectedAccountIdWithCloud}
      handleCloudAccountChange={handleCloudAccountChange}
      hubspotCloudSelector={<></>}
      isCloudDisabled={!isEmpty(cloudAccountId)}
      accountId={currentAccountId}
    />
  )
}

export const SalesforceResellerAuthorizationCreation: React.FC = () => {
  const { partnerId, productId } = useParams<{
    partnerId?: string
    productId: string
  }>()
  const location = useLocation()
  const { search } = location
  const { record, actions } = useSalesforceContext()

  useCloseSalesforceModalAfterSave({
    resourceType: 'reseller-auth',
    closeModal: actions?.closeModal,
  })

  // Extract CRM opportunity ID from Salesforce context or URL params
  const crmOpportunityId =
    record?.recordId ||
    new URLSearchParams(search).get('hs_object_id')?.toLowerCase() ||
    ''
  // Extract CRM object link from URL params
  const crmObjectLink = new URLSearchParams(search).get('object_link') || ''

  const { partnerData } = useSelector<AccountDataState>(
    state => state.AccountData.user as AccountDataState
  )

  const fallbackAccountId = useAccountId({ product: 'flyout' }) || ''
  const cloudAccountId = new URLSearchParams(search).get('cloudAccountId') || ''
  const flyoutAccountId = cloudAccountId || fallbackAccountId
  const cloudMarketplace = useCurrentCloudProvider()

  const dispatch = useDispatch()
  const loadPartnerData = async () => {
    try {
      if (isEmpty(partnerData) || partnerData?.partnerId !== partnerId) {
        await dispatch(
          getAccount(partnerId as string, PartnerType.User) as never
        )
      }
      await dispatch(loadAvailableAccounts(partnerId as string) as never)
    } catch (error) {
      console.error('Failed to load partner data:', error)
    }
  }

  useEffect(() => {
    if (isEmpty(fallbackAccountId)) {
      loadPartnerData()
    }
  }, [fallbackAccountId])

  useEffect(() => {
    if (partnerId && cloudMarketplace) {
      dispatch(
        setSelectedAccount(partnerId, 'flyout', cloudMarketplace as CloudType)
      )
    }
  }, [partnerId, cloudMarketplace, dispatch])

  // Construct externalBaseURL for Salesforce
  const externalBaseURL = useMemo(
    () =>
      (flyoutAccountId || partnerId) && productId
        ? `/external-apps/salesforce/partners/${flyoutAccountId || partnerId}/flyout/products-listing/${productId}`
        : '',
    [flyoutAccountId, partnerId, productId]
  )

  const creationTitle = 'Create a Reseller authorization'

  const CreationFormComponent = useMemo(
    () => () =>
      cloudMarketplace === 'AZURE' ? (
        <SalesforceResellerAuthorizationCreationAzure
          crmOpportunityId={crmOpportunityId}
          externalBaseURL={externalBaseURL}
          search={search}
        />
      ) : (
        <SalesforceEditResaleAuthorizationV2
          externalBaseURL={externalBaseURL}
          crmOpportunityId={crmOpportunityId}
          search={search}
          crmObjectLink={crmObjectLink}
        />
      ),
    [cloudMarketplace, crmOpportunityId, externalBaseURL, search, crmObjectLink]
  )

  return (
    <LayoutWrapper
      title={creationTitle}
      component={CreationFormComponent}
      isBack={false}
      actions={SelectedCloudIcon}
      isSidePanelOpened={false}
      isExternal
    />
  )
}

export const SalesforceResellerAuthorizationEdit: React.FC = () => {
  const {
    partnerId: partnerIdFromParams,
    productId,
    resaleAuthorizationId,
  } = useParams<{
    partnerId?: string
    productId: string
    resaleAuthorizationId: string
  }>()
  const location = useLocation()
  const { search } = location
  const { record, partnerId, actions } = useSalesforceContext()
  const dispatch = useDispatch()
  useCloseSalesforceModalAfterSave({
    resourceType: 'reseller-auth',
    closeModal: actions?.closeModal,
  })

  const { partnerData } = useSelector<AccountDataState>(
    state => state.AccountData.user as AccountDataState
  )
  const fallbackAccountId = useAccountId({ product: 'flyout' }) || ''
  const cloudAccountId = new URLSearchParams(search).get('cloudAccountId') || ''
  const flyoutAccountId = cloudAccountId || fallbackAccountId
  const cloudMarketplace = useCurrentCloudProvider()
  const crmOpportunityId = useMemo(
    () =>
      record?.recordId ||
      new URLSearchParams(search).get('crm_opportunity_id') ||
      new URLSearchParams(search).get('hs_object_id')?.toLowerCase() ||
      '',
    [record?.recordId, search]
  )
  const fromValue = new URLSearchParams(search).get('from') || ''
  // Extract CRM object link from URL params
  const crmObjectLink = useMemo(
    () => new URLSearchParams(search).get('object_link') || '',
    [search]
  )

  // Construct externalBaseURL for Salesforce
  const externalBaseURL = useMemo(
    () =>
      (flyoutAccountId || partnerId) && productId
        ? `/external-apps/salesforce/partners/${flyoutAccountId || partnerId}/flyout/products-listing/${productId}`
        : '',
    [flyoutAccountId, partnerId, productId]
  )

  // Load partner data if needed
  useEffect(() => {
    const loadPartnerData = async () => {
      if (!partnerId) return

      try {
        // Load partner account data if not already loaded or if different partnerId
        if (isEmpty(partnerData) || partnerData?.partnerId !== partnerId) {
          await dispatch(
            getAccount(partnerId as string, PartnerType.User) as never
          )
        }

        // Load available accounts for account selection
        await dispatch(loadAvailableAccounts(partnerId as string) as never)
      } catch (error) {
        console.error('Failed to load partner data:', error)
      }
    }

    if (isEmpty(fallbackAccountId)) {
      void loadPartnerData()
    }
  }, [fallbackAccountId, partnerId, partnerData?.partnerId, dispatch])

  useEffect(() => {
    if (flyoutAccountId && cloudMarketplace) {
      dispatch(
        setSelectedAccount(
          flyoutAccountId,
          'flyout',
          cloudMarketplace as CloudType
        )
      )
    }
  }, [flyoutAccountId, cloudMarketplace, dispatch])

  // Memoize the component to prevent unnecessary re-renders
  const Component = useMemo(
    () => () =>
      cloudMarketplace === 'AZURE' ? (
        <SalesforceResellerAuthorizationCreationAzure
          crmOpportunityId={crmOpportunityId}
          externalBaseURL={externalBaseURL}
          search={search}
        />
      ) : (
        <SalesforceEditResaleAuthorizationV2
          externalBaseURL={externalBaseURL}
          search={search}
          crmOpportunityId={crmOpportunityId}
          crmObjectLink={crmObjectLink}
          isSidePanelVisible={fromValue === 'clone'}
        />
      ),
    [
      cloudMarketplace,
      externalBaseURL,
      search,
      crmOpportunityId,
      crmObjectLink,
      fromValue,
    ]
  )

  return (
    <LayoutWrapper
      title={`${fromValue === 'clone' ? 'Clone' : 'Update'} Reseller authorization`}
      component={Component}
      isBack={false}
      actions={SelectedCloudIcon}
      isSidePanelOpened={false}
      isExternal={fromValue !== 'clone'}
    />
  )
}

export const SalesforceViewResaleAuthorization: React.FC = () => {
  const selectedCloud = useCurrentCloudProvider()
  const { classes } = useCommonStyles()
  const { partnerId: partnerIdFromParams } = useParams<{
    partnerId: string
  }>()
  const { record } = useSalesforceContext()
  const { search } = useLocation()
  const currentPartnerId = partnerIdFromParams || ''
  const crmOpportunityId =
    record?.recordId ||
    new URLSearchParams(search).get('crm_opportunity_id') ||
    new URLSearchParams(search).get('hs_object_id')?.toLowerCase() ||
    ''
  const { productId, resaleAuthorizationId } = useParams<{
    productId: string
    resaleAuthorizationId: string
  }>()

  const isFetchLoading = useSelector<boolean>(state =>
    Loading(state.loading)(LoadingTypes.GET_SINGLE_RESALE_AUTHORIZATION)
  )
  const dispatch = useDispatch()
  const externalBaseURL = `/external-apps/salesforce/partners/${currentPartnerId}/flyout/products-listing/${productId}`
  useEffect(() => {
    const loadCurrentResaleAuthorization = async () => {
      if (!resaleAuthorizationId || !currentPartnerId) return

      try {
        await dispatch(
          getCurrentResaleAuthorization({
            cloud: selectedCloud,
            partnerId: currentPartnerId,
            resaleAuthorizationId,
            crmOpportunityId,
            productId,
          })
        )
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to load current resale authorization', err)
      }
    }

    loadCurrentResaleAuthorization()
  }, [
    resaleAuthorizationId,
    selectedCloud,
    productId,
    crmOpportunityId,
    dispatch,
  ])

  if (isFetchLoading) {
    return (
      <div className={classes.loadingWrapper}>
        <LoadingLogo style={{ height: 'auto' }} />
      </div>
    )
  }

  return (
    <ViewSentResellerAuthorization
      crmOpportunityId={crmOpportunityId}
      externalBaseURL={externalBaseURL}
      search={search}
    />
  )
}
